import { useCallback, useState } from "react";
import { initialChecklistServicoLista, useChecklistServicoListaFilter } from "../hooks/ChecklistServico_lista/filters";
import { useChecklistServicoListaPagination } from "../hooks/ChecklistServico_lista/pagination";
import { ChecklistServico } from "../../domain/models/checklist_servico";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { ChecklistServicoForm } from "../schemas/ChecklistServico_lista/checklist_servico";
import { CopyChecklistServicoForm } from "../schemas/ChecklistServico_lista/copy_checklist_servico";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

/**
 * LoadChecklistServicoFuncState
 */

type LoadChecklistServicoFuncState = {
  tipoCampo?: number;
  valorCampo?: string | null;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useChecklistServicoListaViewModel
 */

export const useChecklistServicoListaViewModel = () => {
  const {
    obras: {
      getAllChecklistServicoUseCase,
      createChecklistServicoUseCase,
      updateChecklistServicoUseCase,
      deleteChecklistServicoUseCase,
      copyChecklistServicoUseCase,
    },
    reports: {
      getReportListaChecklistServicoUseCase,
      getReportListaChecklistServicoItemUseCase,
    }
  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [checklistServicos, setChecklistServicos] = useState<ChecklistServico[]>();

  const [selectedChecklistServico, setSelectedChecklistServico] = useState<ChecklistServico>();

  const [checklistServicoModal, setChecklistServicoModal] = useState(false);
  const [checklistServicoDeleteModal, setChecklistServicoDeleteModal] = useState(false);
  const [copyChecklistServicoModal, setCopyChecklistServicoModal] = useState(false);

  const filters = useChecklistServicoListaFilter();
  const pagination = useChecklistServicoListaPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funcoes de carregamento
   */

  const loadChecklistServicos = useCallback(async (fs?: LoadChecklistServicoFuncState) => {
    try {
      const filteredTipoCampo = evaluateParamValue(fs?.tipoCampo, filters.tipoCampo);
      const filteredValorCampo = evaluateParamValue(fs?.valorCampo, filters.valorCampo);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllChecklistServicoUseCase.getAllChecklistServico(
        filteredTipoCampo,
        filteredValorCampo,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setChecklistServicos(getAllChecklistServicoUseCase.items);
      pagination.setTotalItems(getAllChecklistServicoUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error('Falha ao carregar dados da pagina!');
    }
  }, [
    getAllChecklistServicoUseCase,
    filters,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadChecklistServicos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadChecklistServicos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistServicos,
    pushErrorNotification,
  ]);

  const handleChecklistServicoSubmit = useCallback(async (form: ChecklistServicoForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedChecklistServico) {
        await updateChecklistServicoUseCase.updateChecklistServico(
          selectedChecklistServico.idChecklistServico,
          form.ativo,
          form.codigo,
          form.nroRevisao,
          form.descricao,
          form.tipoServico,
          form.unidade,
          form.campo1,
          form.campo2,
          form.campo3,
          form.campo4,
        );
      } else {
        await createChecklistServicoUseCase.createChecklistServico(
          form.ativo,
          form.codigo,
          form.nroRevisao,
          form.descricao,
          form.tipoServico,
          form.unidade,
          form.campo1,
          form.campo2,
          form.campo3,
          form.campo4,
        );
      }

      await loadChecklistServicos();

      setChecklistServicoModal(false);
      setSelectedChecklistServico(undefined);
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar checklist de serviço!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedChecklistServico,
    createChecklistServicoUseCase,
    updateChecklistServicoUseCase,
    loadChecklistServicos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleChecklistServicoDeleteSubmit = useCallback(async () => {
    if (selectedChecklistServico) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteChecklistServicoUseCase.deleteChecklistServico(
          selectedChecklistServico.idChecklistServico,
        );

        await loadChecklistServicos();

        setChecklistServicoDeleteModal(false);
        setSelectedChecklistServico(undefined);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir checklist de serviço!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedChecklistServico,
    deleteChecklistServicoUseCase,
    loadChecklistServicos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleCopyChecklistServicoSubmit = useCallback(async (form: CopyChecklistServicoForm) => {
    if (selectedChecklistServico) {
      try {
        setSaving(true);
        setPageSaving(true);

        await copyChecklistServicoUseCase.copyChecklistServico(
          selectedChecklistServico.idChecklistServico,
          form.codigo,
          form.nroRevisao,
          form.descricao,
        );

        await loadChecklistServicos();

        setCopyChecklistServicoModal(false);
        setSelectedChecklistServico(undefined);

        pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao copiar checklist de serviço!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedChecklistServico,
    copyChecklistServicoUseCase,
    loadChecklistServicos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleTipoCampoChange = useDebouncedCallback(debouncedHandler, (tipoCampo: number) => {
    useChecklistServicoListaFilter.setState({ tipoCampo });
    return async () => {
      try {
        setLoading(true);
        useChecklistServicoListaPagination.setState({ page: 1 });
        await loadChecklistServicos({ tipoCampo, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadChecklistServicos,
  ]);

  const handleValorCampoChange = useDebouncedCallback(debouncedHandler, (valorCampo: string | null) => {
    useChecklistServicoListaFilter.setState({ valorCampo });
    return async () => {
      try {
        setLoading(true);
        useChecklistServicoListaPagination.setState({ page: 1 });
        await loadChecklistServicos({ valorCampo, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadChecklistServicos,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandler, (status: number) => {
    useChecklistServicoListaFilter.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useChecklistServicoListaPagination.setState({ page: 1 });
        await loadChecklistServicos({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadChecklistServicos,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useChecklistServicoListaFilter.setState({ ...initialChecklistServicoLista })
      useChecklistServicoListaPagination.setState({ page: 1 });
      await loadChecklistServicos({ ...initialChecklistServicoLista, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistServicos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useChecklistServicoListaPagination.setState({ page: page + 1 });
      await loadChecklistServicos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistServicos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useChecklistServicoListaPagination.setState({ page: 1, rowsPerPage });
      await loadChecklistServicos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistServicos,
    pushErrorNotification,
  ]);

  const handleChecklistServicoModal = useCallback((idChecklistServico: number | null, openState: boolean) => {
    if (openState) {
      if (idChecklistServico && checklistServicos) {
        setSelectedChecklistServico(checklistServicos.find(x => x.idChecklistServico === idChecklistServico));
      } else {
        setSelectedChecklistServico(undefined);
      }
    }
    setChecklistServicoModal(openState);
  }, [
    checklistServicos,
  ]);

  const handleChecklistServicoDeleteModal = useCallback((idChecklistServico: number | null, openState: boolean) => {
    if (openState) {
      if (idChecklistServico && checklistServicos) {
        setSelectedChecklistServico(checklistServicos.find(x => x.idChecklistServico === idChecklistServico));
      } else {
        setSelectedChecklistServico(undefined);
      }
    }
    setChecklistServicoDeleteModal(openState);
  }, [
    checklistServicos,
  ]);

  const handleCopyChecklistServicoModal = useCallback((idChecklistServico: number | null, openState: boolean) => {
    if (openState) {
      if (idChecklistServico && checklistServicos) {
        setSelectedChecklistServico(checklistServicos.find(x => x.idChecklistServico === idChecklistServico));
      } else {
        setSelectedChecklistServico(undefined);
      }
    }
    setCopyChecklistServicoModal(openState);
  }, [
    checklistServicos,
  ]);

  const handleEnterChecklist = useCallback((idChecklistServico: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/ChecklistServicoAmbiente_lista?id=${idChecklistServico}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/ChecklistServicoAmbiente_lista?id=${idChecklistServico}`, { alternativeRoute: `/ChecklistServico_lista` });
    }
  }, [
    backOrNavigate,
  ]);

  const handleEnterVisualize = useCallback((idChecklistServico: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/ChecklistServico_obrasItem?id=${idChecklistServico}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/ChecklistServico_obrasItem?id=${idChecklistServico}`, { alternativeRoute: `/ChecklistServico_lista` });
    }
  }, [
    backOrNavigate,
  ]);

  const handleExportar = useCallback(async (idChecklistServico: number) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportListaChecklistServicoItemUseCase.getReportListaChecklistServicoItem(
            requiredMimeType, idChecklistServico)
      });

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio!'))
    } finally {
      setLoading(false);
    }
  }, [
    getReportListaChecklistServicoItemUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleRelatorioLista = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportListaChecklistServicoUseCase.getReportListaChecklistServico(requiredMimeType),
      });

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio!'))
    } finally {
      setLoading(false);
    }
  }, [
    getReportListaChecklistServicoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleEnterChecklistLiberacao = useCallback((idChecklistServico: number | null, newTabOpen: boolean) => {
    if (newTabOpen) {
      window.open(`/ChecklistServicoLiberacao_lista?id=${idChecklistServico}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/ChecklistServicoLiberacao_lista?id=${idChecklistServico}`, { alternativeRoute: `/ChecklistServico_lista` });
    }
  }, [
    backOrNavigate,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    checklistServicos: checklistServicos ?? [],
    selectedChecklistServico,
    checklistServicoModal,
    checklistServicoDeleteModal,
    copyChecklistServicoModal,
    filters,
    pagination,
    reportViewerState,
    handleReload,
    handleChecklistServicoSubmit,
    handleChecklistServicoDeleteSubmit,
    handleCopyChecklistServicoSubmit,
    handleTipoCampoChange,
    handleValorCampoChange,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleChecklistServicoModal,
    handleChecklistServicoDeleteModal,
    handleCopyChecklistServicoModal,
    handleEnterChecklist,
    handleEnterVisualize,
    handleExportar,
    handleRelatorioLista,
    handleEnterChecklistLiberacao,
  };
}